import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { useGoogleMaps } from "../../context/GoogleMapsContext";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import FormField from "../FormInputs/FormField";
import {
  ToggleCard,
  PrimaryButton,
  SecondaryButton,
  FormFieldsSkeleton,
} from "../UI/DesignStandards";
import AddCenterForm from "../TournamentLocations/AddCenterForm";
import SquadTimeSelect from "./SquadTimeSelect";
import { normalizeSquadTime } from "../../utility/createSquad";
import {
  listBowlingCentersForTournament,
  organizationIdForTournament,
} from "../../utility/organizationBowlingCenters";
import { replaceSquadEvents } from "../../utility/squadEventNames";

const CreateSquad = ({
  tournamentID,
  onSquadCreated,
  onCancel,
  editMode = false,
  squadToEdit = null,
  nested = false,
}) => {
  const { user } = useAuth();
  const { isLoaded, loadError } = useGoogleMaps();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tournamentEvents, setTournamentEvents] = useState([]);
  const [orgHouses, setOrgHouses] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [eventTemplates, setEventTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [squadTime, setSquadTime] = useState("");
  const [squadDate, setSquadDate] = useState("");
  const [eventIds, setEventIds] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showAddHouse, setShowAddHouse] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadHouses = useCallback(async () => {
    const centers = await listBowlingCentersForTournament(tournamentID);
    setOrgHouses(centers);
    return centers;
  }, [tournamentID]);

  useEffect(() => {
    const fetchTournamentData = async () => {
      try {
        // Fetch tournament events
        const { data: eventsData, error: eventsError } = await supabase
          .from("tournamentEvents")
          .select("*")
          .eq("tournamentID", tournamentID)
          .eq("isPrimary", true)
          .eq("isDeleted", false);

        if (eventsError) throw eventsError;
        setTournamentEvents(eventsData);

        // Fetch per-squad event templates
        const { data: templatesData, error: templatesError } =
          await supabase.rpc("get_squad_event_templates", {
            p_tournament_id: tournamentID,
          });

        if (templatesError) {
          console.error("Error fetching templates:", templatesError);
          // Don't throw - templates are optional
        } else {
          const templates = templatesData || [];
          setEventTemplates(templates);
          if (!editMode) {
            setSelectedTemplates([]);
          }
        }

        const [orgId, houses] = await Promise.all([
          organizationIdForTournament(tournamentID),
          loadHouses(),
        ]);
        setOrganizationId(orgId);

        if (editMode && squadToEdit) {
          setSquadTime(normalizeSquadTime(squadToEdit.squadTime));
          setSquadDate(squadToEdit.squadDate || "");
          setSelectedLocation(squadToEdit.squadLocation || "");

          // Fetch existing event instances for this squad
          const { data: instancesData, error: instancesError } =
            await supabase.rpc("get_squad_event_instances", {
              p_squad_id: squadToEdit.id,
            });

          if (!instancesError && instancesData) {
            // Set selected templates to the template IDs of existing instances
            setSelectedTemplates(
              instancesData.map((instance) => instance.template_id),
            );
          }

          const { data: eventRows, error: eventError } = await supabase
            .from("squadEvents")
            .select("eventsID")
            .eq("squadID", squadToEdit.id);
          setEventIds(
            ((eventError ? [] : eventRows) || [])
              .map((row) => row.eventsID)
              .filter(Boolean)
          );
        } else {
          if ((eventsData || []).length === 1) {
            setEventIds([eventsData[0].id]);
          }
          if (houses.length === 1) {
            setSelectedLocation(houses[0].bowlingCenters.id);
          }
        }

        if (houses.length === 0) {
          setShowAddHouse(true);
        }
      } catch (error) {
        console.error("Error fetching tournament data:", error);
        setErrorMessage("Error fetching tournament data");
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournamentID, editMode, squadToEdit, loadHouses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    if (!squadTime || !squadDate || !eventIds.length || !selectedLocation) {
      setErrorMessage("Squad time, date, at least one event, and location are required.");
      setSaving(false);
      return;
    }

    const squadData = {
      squadTime,
      squadDate,
      tournamentID,
      squadLocation: selectedLocation,
    };

    try {
      let squadId;

      if (editMode) {
        const { error } = await supabase
          .from("squads")
          .update({
            squadTime,
            squadDate,
            squadLocation: selectedLocation,
          })
          .eq("id", squadToEdit.id);

        if (error) throw error;
        squadId = squadToEdit.id;

        // Handle template changes in edit mode
        if (selectedTemplates.length > 0 || eventTemplates.length > 0) {
          // Get current instances
          const { data: currentInstances } = await supabase.rpc(
            "get_squad_event_instances",
            { p_squad_id: squadId },
          );

          const currentTemplateIds = (currentInstances || []).map(
            (i) => i.template_id,
          );

          // Find templates to add (in selected but not in current)
          const templatesToAdd = selectedTemplates.filter(
            (tid) => !currentTemplateIds.includes(tid),
          );

          // Find templates to remove (in current but not in selected)
          const templatesToRemove = currentTemplateIds.filter(
            (tid) => !selectedTemplates.includes(tid),
          );

          // Add new templates
          if (templatesToAdd.length > 0) {
            const { error: addError } = await supabase.rpc(
              "create_squad_event_instances",
              {
                p_squad_id: squadId,
                p_template_ids: templatesToAdd,
                p_custom_names: null,
              },
            );
            if (addError) throw addError;
          }

          // Remove templates
          if (templatesToRemove.length > 0) {
            const { error: removeError } = await supabase.rpc(
              "delete_squad_event_instances",
              {
                p_squad_id: squadId,
                p_template_ids: templatesToRemove,
              },
            );
            if (removeError) throw removeError;
          }
        }
      } else {
        const { data, error } = await supabase
          .from("squads")
          .insert([squadData])
          .select();

        if (error) throw error;
        squadId = data[0].id;

        // Create event instances for selected templates
        if (selectedTemplates.length > 0) {
          const { error: instancesError } = await supabase.rpc(
            "create_squad_event_instances",
            {
              p_squad_id: squadId,
              p_template_ids: selectedTemplates,
              p_custom_names: null,
            },
          );
          if (instancesError) throw instancesError;
        }
      }

      await replaceSquadEvents(squadId, eventIds);

      onSquadCreated();
    } catch (error) {
      console.error("Error saving squad:", error);
      setErrorMessage(error?.message || "Error saving squad");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={nested ? "card-inset p-6" : "card-board p-6"}>
        <h2 className="heading-display text-xl mb-6">
          {editMode ? "Edit Squad" : "Create New Squad"}
        </h2>
        <FormFieldsSkeleton fields={5} />
      </div>
    );
  }

  return (
    <div className={nested ? "card-inset p-6" : "card-board p-6"}>
      {errorMessage ? (
        <div className="mb-3">
          <InlineAlert
            type="error"
            title="Please fix the following"
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        </div>
      ) : null}
      <h2 className="heading-display text-xl mb-6">
        {editMode ? "Edit Squad" : "Create New Squad"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm font-medium text-brand-charcoal mb-1">
            Events
          </p>
          <p className="text-xs text-brand-muted mb-3">
            Which events bowl on this squad.
          </p>
          {tournamentEvents.length === 0 ? (
            <p className="text-sm text-brand-muted">
              Add events on Setup, then choose them here.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tournamentEvents.map((event) => {
                const checked = eventIds.includes(event.id);
                return (
                  <ToggleCard
                    key={event.id}
                    checked={checked}
                    onChange={() => {
                      setEventIds((prev) =>
                        checked
                          ? prev.filter((id) => id !== event.id)
                          : [...prev, event.id]
                      );
                    }}
                    title={event.eventName}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div>
          <FormField
            label="Location"
            isSelect={true}
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            options={orgHouses.map((house) => ({
              value: house.bowlingCenters.id,
              label: house.bowlingCenters.centerName,
            }))}
            required={true}
          />
          {loadError ? (
            <p className="text-sm text-brand-muted mb-2">
              Could not load Google Maps. Add a house from the organization
              bowling centers page.
            </p>
          ) : isLoaded && organizationId ? (
            <div
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
            >
              <AddCenterForm
                showForm={showAddHouse}
                onToggleForm={() => setShowAddHouse(!showAddHouse)}
                organizationId={organizationId}
                onCenterAdded={async (center) => {
                  const houses = await loadHouses();
                  const nextId =
                    center?.id ||
                    houses[houses.length - 1]?.bowlingCenters?.id ||
                    "";
                  if (nextId) setSelectedLocation(nextId);
                  setShowAddHouse(false);
                }}
                isLoggedIn={!!user}
                canManage
              />
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-brand-charcoal">
            Squad Time
          </label>
          <SquadTimeSelect value={squadTime} onChange={setSquadTime} />
        </div>

        <FormField
          label="Squad Date"
          type="date"
          value={squadDate}
          onChange={(e) => setSquadDate(e.target.value)}
        />

        {/* Per-Squad Event Templates */}
        {eventTemplates.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-brand-charcoal mb-2">
              Per-Squad Events (Optional)
            </label>
            <p className="text-xs text-brand-muted mb-3">
              Select event templates to create squad-specific side pots for this
              squad.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {eventTemplates.map((template) => {
                const isSelected = selectedTemplates.includes(template.id);
                return (
                  <ToggleCard
                    key={template.id}
                    checked={isSelected}
                    onChange={() => {
                      if (isSelected) {
                        setSelectedTemplates(
                          selectedTemplates.filter((id) => id !== template.id),
                        );
                      } else {
                        setSelectedTemplates([
                          ...selectedTemplates,
                          template.id,
                        ]);
                      }
                    }}
                    title={template.event_name}
                    subtitle={
                      template.event_abbreviation
                        ? `(${template.event_abbreviation})`
                        : undefined
                    }
                  />
                );
              })}
            </div>
            {selectedTemplates.length > 0 && (
              <p className="mt-2 text-sm text-brand-muted">
                {selectedTemplates.length} per-squad event
                {selectedTemplates.length !== 1 ? "s" : ""} selected
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <SecondaryButton
            type="button"
            onClick={onCancel}
            className="!px-3 !py-2 text-sm"
            disabled={saving}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" loading={saving} disabled={saving}>
            {saving ? "Saving..." : editMode ? "Update Squad" : "Create Squad"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};

export default CreateSquad;
